import React, { useEffect, useRef, useState } from "react";
import axiosInstance from "../../../api/axiosInstance";
import { backendUrl } from "../../../api/constants";
import EditEvent from "../../../components/ui/EditEvent";
import { usePortalContext } from "../../../context/PortalContext/portalcontext";
import { IoIosAddCircle } from "react-icons/io";
import { IoIosArrowBack } from "react-icons/io";
import Input from "../../../components/Input/Input";
import Button from "../../../components/Button/Button";
import { createModal } from "../../../util/Modal";
import { styles } from "../../../components/PortalStyles/portalStyles.style";
import { enqueueSnackbar } from "notistack";
import { PortalTitle } from "../../Portal";
import TemplateCard from "../../../components/TemplateCard/TemplateCard";
import PerformerSlotsEditor from "../../../components/PerformerSlotsEditor/PerformerSlotsEditor";
import { ROSTER_FEATURE_ENABLED } from "../../../config/features";
import { useUser } from "../../../context/UserContext/UserProvider";

const Templates = () => {
  const [templates, setTemplates] = useState();
  const [selectedTemplate, setSelectedTemplate] = useState();
  const [postError, setPostError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [liveEventDate, setLiveEventDate] = useState(null);
  const { userLocation, setEditEvent, setCurrentTab } = usePortalContext();
  const { user } = useUser();
  const templateNameRef = useRef();
  const eventRef = useRef();
  const slotsRef = useRef();
  const showPerformerSlots = ROSTER_FEATURE_ENABLED && user?.accountType === "business";

  const fetchTemplates = async () => {
    try {
      const { data } = await axiosInstance.get(
        `${backendUrl}/influencer/templates`
      );
      if (data?.error) {
        enqueueSnackbar(data.error, { variant: "error" });
      } else {
        setTemplates(data);
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar("something went wrong, please try again later", {
        variant: "error",
      });
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const saveTemplate = async () => {
    if (
      !templateNameRef.current.value ||
      templateNameRef.current.value.trim() === ""
    ) {
      enqueueSnackbar("please enter a template name", { variant: "error" });
      return setPostError("please enter a template name");
    }

    const { form, error } = await eventRef.current?.getFormData();
    if (error) {
      enqueueSnackbar(error, { variant: "error" });
      setPostError(error);
      return;
    }

    if (showPerformerSlots && slotsRef.current && form?.has("event")) {
      const event = JSON.parse(form.get("event") || "{}");
      const slotsData = slotsRef.current.getSlotsData(event.date);
      if (slotsData.error) {
        enqueueSnackbar(slotsData.error, { variant: "error" });
        setPostError(slotsData.error);
        return;
      }
      event.roster = slotsData.roster;
      event.performerSlots = slotsData.performerSlots.filter(
        (slot) => slot.title?.trim() || slot.start || slot.end
      );
      form.set("event", JSON.stringify(event));
    }

    form.append("templateName", templateNameRef.current.value);

    try {
      const { data: res } = await axiosInstance.post(
        `${backendUrl}/influencer/templates/save`,
        form
      );
      if (res?.error) {
        if (typeof res.error === "string") {
          enqueueSnackbar(res.error, { variant: "error" });
          setPostError(res.error);
          return;
        } else {
          for (const key in res.error) {
            if (res.error[key].hasOwnProperty("error")) {
              if (key === "address") {
                eventRef.current?.setAddressErrors?.(res.error[key].error || res.error[key]);
              }
              enqueueSnackbar(res.error[key].error, { variant: "error" });
              setPostError(res.error[key].error);
              return;
            }
          }
        }
      } else {
        setPostError(null);
        setSelectedTemplate(null);
        enqueueSnackbar("changes saved successfully", { variant: "success" });
        fetchTemplates();
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar("something went wrong, please try again later", { variant: "error" });
      setPostError("something went wrong, please try again later");
      return;
    }
  };

  const deleteTemplate = async (template) => {
    const result = await createModal(({ onModalResult }) => (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--inyc-space-20)",
        }}
      >
        <div style={{ fontFamily: "Primary-Bold", fontSize: "var(--inyc-text-md)" }}>
          are you sure you want to delete this template?
        </div>
        <div
          style={{
            marginTop: "var(--inyc-space-20)",
            display: "flex",
            gap: "var(--inyc-space-20)",
            justifyContent: "center",
          }}
        >
          <Button
            fontSize="0.85rem"
            btnClassName="!bg-white !text-black !border-black !py-kit-4 !leading-[1.2] [:is(body.dark)_&]:!border-white"
            onClick={() => onModalResult(false)}
          >
            no
          </Button>
          <Button
            fontSize="0.85rem"
            btnClassName="!bg-black !text-white !border-[var(--inyc-outline-color)] !py-kit-4 !leading-[1.2] [:is(body.dark)_&]:!border-[var(--inyc-outline-color)]"
            onClick={() => onModalResult(true)}
          >
            yes
          </Button>
        </div>
      </div>
    ));

    if (!result) {
      return;
    }
    try {
      const { data } = await axiosInstance.get(
        `${backendUrl}/influencer/templates/delete/${template._id}`
      );
      if (data?.error) {
        enqueueSnackbar(data.error, { variant: "error" });
      } else {
        enqueueSnackbar("template deleted", { variant: "success" });
        fetchTemplates();
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar("something went wrong", { variant: "error" });
    }
  };

  const handlePost = (template) => {
    const { _id, templateName, type, ...rest } = template;
    setEditEvent(rest);
    setCurrentTab("new-event");
  };

  const handleEdit = (template) => {
    setLiveEventDate(template?.date || null);
    setSelectedTemplate(template);
  };

  const handleDuplicate = (template) => {
    const { _id, ...rest } = template;
    setLiveEventDate(rest?.date || null);
    setSelectedTemplate(rest);
  };

  return (
    <div className="bg-[var(--inyc-primary)] min-h-[100vh]">
      <PortalTitle>templates</PortalTitle>
      
      {/* Header Bar */}
      <div className={`mb-kit-16 w-full ${selectedTemplate ? "max-w-[1200px]" : "max-w-[420px]"} mx-auto`}>
        {selectedTemplate ? (
          <div className="flex flex-col gap-kit-16">
            {/* Cancel Button */}
            <button
              type="button"
              className="arrow-events-container cursor-pointer p-kit-8"
              onClick={() => {
                setLiveEventDate(null);
                setSelectedTemplate(null);
              }}
            >
              <IoIosArrowBack className="arrow-icon" size={20} />
            </button>
            
            {/* Template Name Section - clearly separated */}
            <div className="template-name-section pt-kit-16 pb-kit-24 px-kit-16 rounded-kit-lg border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary-light)]/30">
              <div className="ui-label mb-kit-8">template name</div>
              <Input
                defaultValue={selectedTemplate?.templateName}
                className="w-full"
                placeHolder="template name"
                Ref={templateNameRef}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-kit-16 px-kit-12 pt-kit-4">
            <button
              onClick={() => {
                setLiveEventDate(null);
                setSelectedTemplate({});
              }}
              className="flex shrink-0 items-center justify-center gap-kit-8 cursor-pointer rounded-kit-full bg-[var(--inyc-input-background-color)] px-kit-16 py-kit-8 text-kit-xs font-['Primary-Bold'] text-[var(--inyc-primary-text-color)] transition-colors duration-200 hover:bg-[var(--inyc-primary-light)]"
            >
              <IoIosAddCircle size={16} />
              new template
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className={`${selectedTemplate ? "max-w-[1200px]" : "max-w-[420px]"} mx-auto`}>
        {selectedTemplate ? (
          <>
            <EditEvent
              Ref={eventRef}
              userLocation={userLocation}
              edit={selectedTemplate}
              noPortalTitle={true}
              onDateChange={setLiveEventDate}
            />
            {showPerformerSlots && (
              <PerformerSlotsEditor
                ref={slotsRef}
                initialData={selectedTemplate}
                eventDate={liveEventDate || selectedTemplate?.date}
              />
            )}
            <Button
              error={postError}
              prompt
              message="confirm?"
              style={styles.PostButton}
              loading={loading}
              onClick={saveTemplate}
            >
              {selectedTemplate?._id ? "save changes" : "save template"}
            </Button>
          </>
        ) : (
          <div className="flex flex-col gap-kit-16 px-kit-12">
            {templates?.map((template) => (
              <TemplateCard
                key={template._id}
                template={template}
                variant="templatesPage"
                onPost={handlePost}
                onEdit={handleEdit}
                onDuplicate={handleDuplicate}
                onDelete={deleteTemplate}
              />
            ))}
            
            {/* Empty state */}
            {templates && templates.length === 0 && (
              <div className="text-center py-kit-64 text-[var(--inyc-primary-text-color)] opacity-50">
                <div className="text-kit-md mb-kit-8">no templates yet</div>
                <div className="text-kit-sm">click "new template" to create your first one</div>
                    </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Templates;
